import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, HelpCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuoteResponsePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const action = searchParams.get('action');
  
  const [quote, setQuote] = useState(null);
  const [clientName, setClientName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [response, setResponse] = useState(null);
  const [queryText, setQueryText] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showQueryForm, setShowQueryForm] = useState(action === 'query');
  const [showRejectForm, setShowRejectForm] = useState(action === 'reject');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      fetchQuote();
    }
  }, [token]);

  const fetchQuote = async () => {
    try {
      const res = await axios.get(`${API}/api/public/quote-response/${token}`);
      setQuote(res.data.quote);
      setClientName(res.data.client_name || 'Valued Customer');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/public/quote-response/${token}/accept`);
      setResponse({ type: 'accepted', message: res.data.message });
      toast.success('Quote accepted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/public/quote-response/${token}/reject`, {
        reason: rejectReason || null
      });
      setResponse({ type: 'rejected', message: res.data.message });
      toast.info('Quote declined');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuery = async () => {
    if (!queryText.trim()) {
      toast.error('Please enter your query');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/public/quote-response/${token}/query`, {
        query_text: queryText
      });
      setResponse({ type: 'query', message: res.data.message });
      toast.success('Query submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit query');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1F7A78] to-[#005456] flex items-center justify-center">
        <div className="text-white text-center">
          <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4" />
          <p>Loading quote details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1F7A78] to-[#005456] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Link Invalid or Expired</h2>
            <p className="text-stone-500">{error}</p>
            <p className="text-sm text-stone-400 mt-4">
              Please contact Leisure Home Solutions if you need assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (response) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1F7A78] to-[#005456] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            {response.type === 'accepted' && (
              <>
                <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-green-700 mb-2">Quote Accepted!</h2>
              </>
            )}
            {response.type === 'rejected' && (
              <>
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-red-700 mb-2">Quote Declined</h2>
              </>
            )}
            {response.type === 'query' && (
              <>
                <HelpCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-amber-700 mb-2">Query Submitted</h2>
              </>
            )}
            <p className="text-stone-600">{response.message}</p>
            <p className="text-sm text-stone-400 mt-4">
              You may close this window.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1F7A78] to-[#005456] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/phs-logo.png" 
            alt="Park Home Support" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white">Quote Response</h1>
        </div>

        {/* Quote Details Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-[#1F7A78]">Quote: {quote.quote_number}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-stone-600">Dear {clientName},</p>
            
            <div className="bg-stone-50 p-4 rounded-lg space-y-2">
              <p><strong>Property:</strong> {quote.property_address || 'N/A'}</p>
              {quote.scope_of_works && (
                <p><strong>Scope:</strong> {quote.scope_of_works}</p>
              )}
            </div>

            {/* Items Table */}
            {quote.items && quote.items.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#1F7A78] text-white">
                    <tr>
                      <th className="px-4 py-2 text-left">Description</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.items.map((item, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="px-4 py-2">{item.description}</td>
                        <td className="px-4 py-2 text-right">£{parseFloat(item.amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-stone-100 font-bold">
                    <tr>
                      <td className="px-4 py-3">Total</td>
                      <td className="px-4 py-3 text-right text-[#1F7A78] text-lg">
                        £{(quote.total || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {!showQueryForm && !showRejectForm ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-stone-600 mb-6 font-medium">
                Please respond to this quote:
              </p>
              <div className="space-y-3">
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                  onClick={handleAccept}
                  disabled={isSubmitting}
                  data-testid="accept-quote-btn"
                >
                  {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
                  Accept Quote
                </Button>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="destructive"
                    onClick={() => setShowRejectForm(true)}
                    disabled={isSubmitting}
                    data-testid="decline-quote-btn"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Decline
                  </Button>
                  <Button
                    variant="outline"
                    className="border-amber-500 text-amber-600 hover:bg-amber-50"
                    onClick={() => setShowQueryForm(true)}
                    data-testid="query-quote-btn"
                  >
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Query
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : showRejectForm ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Decline Quote
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-stone-600">
                We're sorry to hear you'd like to decline. Please let us know the reason so we can improve our service:
              </p>
              <div className="space-y-2">
                <Label htmlFor="rejectReason">Reason for declining (optional)</Label>
                <Textarea
                  id="rejectReason"
                  placeholder="e.g., Price too high, Found alternative provider, Changed my mind about the work..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                  className="resize-none"
                  data-testid="reject-reason-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowRejectForm(false); setRejectReason(''); }}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleReject}
                  disabled={isSubmitting}
                  data-testid="confirm-reject-btn"
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                  Confirm Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-amber-600 flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Submit a Query
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-stone-600">
                Please describe your query or concern below:
              </p>
              <Textarea
                placeholder="Enter your query here..."
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                rows={5}
                className="resize-none"
              />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowQueryForm(false)}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                  onClick={handleQuery}
                  disabled={isSubmitting || !queryText.trim()}
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Submit Query
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-white/70 text-sm mt-8">
          Leisure Home Solutions Ltd | admin@lhsportal.uk
        </p>
      </div>
    </div>
  );
}
